import React, {useState} from 'react';
import {
    Button, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
    DrawerFooter, DrawerHeader, DrawerOverlay, FormLabel, useToast,
} from "@chakra-ui/react";
import {Form} from "antd";
import {Input} from "@nextui-org/react";
import {
    getAuth, EmailAuthProvider, reauthenticateWithCredential, verifyBeforeUpdateEmail,
} from "firebase/auth";
import Firebase from "../../Firebase/Firebase";
import {doc, getFirestore, updateDoc} from "firebase/firestore";

type Props = {
    changeEmail: boolean;
    closeEmail:  (val: boolean) => void;
};

const firebaseAuth = getAuth(Firebase);
const db           = getFirestore(Firebase);

const FRIENDLY: Record<string, string> = {
    'auth/wrong-password':        'Current password is incorrect.',
    'auth/invalid-credential':    'Current password is incorrect.',
    'auth/user-mismatch':         'Email does not match your account.',
    'auth/too-many-requests':     'Too many attempts. Please try again later.',
    'auth/email-already-in-use':  'That email is already in use by another account.',
    'auth/invalid-email':         'Please enter a valid email address.',
    'auth/requires-recent-login': 'Session expired. Please log out and log back in before changing your email.',
};

const ChangeEmail: React.FC<Props> = ({changeEmail, closeEmail}) => {
    const toast = useToast();
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (values: any) => {
        const user = firebaseAuth.currentUser;
        if (!user) {
            toast({position:'top', title:'Not signed in', status:'error', duration:3000, isClosable:true});
            return;
        }

        setLoading(true);
        try {
            // Re-authenticate with current credentials before sensitive change
            const credential = EmailAuthProvider.credential(values.currentEmail, values.password);
            await reauthenticateWithCredential(user, credential);

            // Send verification email to the new address.
            // Firebase only applies the change after the user clicks the link.
            await verifyBeforeUpdateEmail(user, values.newEmail);

            // Optimistically update Firestore so the admin list reflects the change
            await updateDoc(doc(db, 'users', user.uid), {emailAuth: values.newEmail});

            toast({
                position: 'top',
                title: 'Verification email sent',
                description: `A confirmation link has been sent to ${values.newEmail}. Click it to complete the email change.`,
                status: 'success',
                duration: 8000,
                isClosable: true,
            });
            closeEmail(false);
        } catch (e: any) {
            const msg = FRIENDLY[e?.code] ?? 'Failed to change email. Please try again.';
            toast({position:'top', title:'Error', description: msg, status:'error', duration:4000, isClosable:true});
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer isOpen={changeEmail} placement="right" onClose={() => closeEmail(false)}>
            <DrawerOverlay/>
            <DrawerContent>
                <DrawerCloseButton/>
                <DrawerHeader borderBottomWidth="1px">Change Email Address</DrawerHeader>

                <DrawerBody>
                    <div style={{background:'#fef9c3', border:'1px solid #fde68a', borderRadius:8,
                                 padding:'10px 14px', fontSize:12, color:'#92400e', marginBottom:16}}>
                        A verification link will be sent to your <b>new</b> email. The change takes effect
                        after you click it.
                    </div>
                    <Form onFinish={handleSubmit} id="emailForm" layout="vertical" className="mt-2">
                        <FormLabel htmlFor="currentEmail" className="ml-1">Current email</FormLabel>
                        <Form.Item name="currentEmail"
                            rules={[{required:true, message:'Required'}, {type:'email', message:'Invalid email'}]}>
                            <Input type="email" size="lg" width="100%" onChange={() => {}}
                                   placeholder="Your current email address" className="my-1"/>
                        </Form.Item>

                        <FormLabel htmlFor="password" className="ml-1">Current password</FormLabel>
                        <Form.Item name="password" rules={[{required:true, message:'Required'}]}>
                            <Input.Password size="lg" width="100%" onChange={() => {}}
                                           placeholder="Your current password" className="my-1"/>
                        </Form.Item>

                        <FormLabel htmlFor="newEmail" className="ml-1">New email</FormLabel>
                        <Form.Item name="newEmail"
                            rules={[{required:true, message:'Required'}, {type:'email', message:'Invalid email'}]}>
                            <Input type="email" size="lg" width="100%" onChange={() => {}}
                                   placeholder="Your new email address" className="my-1"/>
                        </Form.Item>
                    </Form>
                </DrawerBody>

                <DrawerFooter borderTopWidth="1px">
                    <Button variant="outline" mr={3} onClick={() => closeEmail(false)}>Cancel</Button>
                    <Button type="submit" form="emailForm" isLoading={loading} colorScheme="blue">
                        Send Verification
                    </Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export default ChangeEmail;
